import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle2, ChevronRight, Sparkles, FileDown, ArrowRight,
  Coffee, GraduationCap, ShoppingBag, Briefcase,
  AlertTriangle, Lock,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";
import useNoIndex from "@/lib/useNoIndex";
import BespokePageRibbon from "@/components/BespokePageRibbon";
import BespokeWatermark from "@/components/BespokeWatermark";
import VideoAd from "@/components/VideoAd";

// Phase 25.13ap — IWG bespoke proposal page.
//
// Built for the Mercedes Bankston (IWG · Tech / Partnership / Venture
// Acceleration) conversation. Triggered by her LinkedIn reply confirming
// the IWG-tenant-network distribution channel + named property-owner
// intros pending. Held in reserve until she returns with names; this
// page is the artefact she can forward to property owners as a
// "DARA × IWG" framing without DARA having to be at the call.
//
// Same Bloomberg-style chrome as /ventia. Logo-free by design (text
// "IWG × DARA OS" mark) for trademark cleanliness + bespoke feel.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VIDEO_SMB = "https://customer-assets.emergentagent.com/job_analytics-vault-5/artifacts/1np457in_DARA%20%E2%80%94%20Enterprise%20AI%20Data%20Monetization%20%281%29.mp4";

const TENANT_TYPES = [
  { Icon: Coffee, name: "Cafés & food merchants", pitch: "POS-driven, daily revenue capture, marginal-pricing-aware. Highest engagement category in DARA's existing SMB cohort." },
  { Icon: ShoppingBag, name: "Micro-retailers & boutiques", pitch: "Inventory + footfall + ticket-size optimisation. Multi-channel reconciliation (in-store + online + marketplace)." },
  { Icon: GraduationCap, name: "Training schools & coaching", pitch: "Cohort enrolment, payment plan analytics, NPS by program. Pricing-tier optimisation per cohort." },
  { Icon: Briefcase, name: "Professional services & consultancies", pitch: "Project margin, time-write-off detection, client-NPS forecasting, capacity vs. pipeline gap analysis." },
];

// Phase 25.13ap-p2 — Four on-ramps for IWG tenants. Honest about MVP vs
// roadmap so prospects don't get surprised mid-pilot. Native connectors +
// CSV are production today; email-in + manual-entry are Q2 2026 roadmap.
const ON_RAMPS = [
  {
    code: "01",
    label: "Native connectors",
    fit: "Tenants on Xero · Square · MyOB · Stripe · Lightspeed · Shopify · Toast",
    flow: "OAuth login → live data flow → first weekly brief in 24h",
    status: "live",
    coverage: "~20-30% of IWG tenants",
  },
  {
    code: "02",
    label: "CSV / spreadsheet upload",
    fit: "Any tenant who uses Excel · Google Sheets · or exports POS reports",
    flow: "Drag-drop CSV weekly → DARA-grounded parse + column profile → weekly brief",
    status: "live",
    coverage: "~70-80% of IWG tenants — the universal path",
  },
  {
    code: "03",
    label: "Email-in forwarding",
    fit: "Tenants whose POS / booking system emails daily reports automatically",
    flow: "Set a forwarding rule → DARA parses email body → weekly brief (zero ongoing effort)",
    status: "roadmap",
    coverage: "~60% of tenants once shipped · Q2 2026",
  },
  {
    code: "04",
    label: "Weekly manual check-in",
    fit: "Cash-only operators · very early-stage SMBs · no formal POS",
    flow: "60-second weekly form (customers · revenue · top sellers) → cohort benchmark brief",
    status: "roadmap",
    coverage: "~10-15% of tenants · Q3 2026",
  },
];

const PILOT_PHASES = [
  { week: "Weeks 1-2", title: "Co-design", body: "Pick one named IWG property. Nominate 15-25 tenant operators. Agree the success bar in writing before anyone touches data." },
  { week: "Weeks 3-8", title: "Deploy", body: "DARA dashboards live for cohort. Weekly DARA-grounded brief per tenant. White-label as 'IWG Member Intelligence' or 'Powered by DARA' — IWG chooses." },
  { week: "Weeks 9-12", title: "Measure", body: "Cohort NPS, conversion-to-paid, retention vs control group. Per-tenant case study. Findings briefed to IWG leadership and to the property-owner cohort." },
];

const DEAL_MODELS = [
  {
    code: "A",
    name: "Pilot · $0 to IWG",
    setup: "$0 fee to IWG · 12-week pilot · DARA captures direct-paying tenants as upside",
    success: "≥70% weekly active · ≥2 of 25 tenants convert to paid",
    risk: "Near-zero. IWG signs no purchase order.",
    upside: "DARA earns $0-$30k from converted tenants · IWG retains attribution",
    highlight: true,
  },
  {
    code: "B",
    name: "Revenue-share",
    setup: "IWG receives a defined % of subscription revenue from any tenant referred through an IWG property · 24-month window",
    success: "Aligned. IWG paid only on actual converted ARR.",
    risk: "Verification + tracking infrastructure already shipped at DARA.",
    upside: "Scales with conversion velocity. Could be $10k-200k+ per property cluster.",
  },
  {
    code: "C",
    name: "Enterprise license",
    setup: "Flat platform fee per-IWG-property · tenant access bundled into IWG membership tiers",
    success: "Predictable IWG-line-item SaaS spend · bundled as tenant-value-add.",
    risk: "Standard SaaS adoption. Best fit once Models A/B prove conversion.",
    upside: "$2-8k per property per year × N properties · long-tail commercial path.",
  },
];

const HONEST_LIMITS = [
  { Icon: AlertTriangle, label: "Most IWG tenants will not log in to a vendor dashboard. We default to a weekly email brief — 90-second read. Dashboard is opt-in for power users.", colour: "text-[#FFB000]" },
  { Icon: AlertTriangle, label: "DARA is a small team. Solo founder, product live, capacity for 1-2 enterprise channel partners in 2026 max. IWG would be one of them.", colour: "text-[#FFB000]" },
  { Icon: Lock, label: "Multi-region sovereign: AU (AWS Sydney) · UK (London) · US (us-east-1). Data never crosses regions. SOC 2 Type I roadmap H2 2026.", colour: "text-[#00E676]" },
  { Icon: CheckCircle2, label: "We don't replace Yardi / MRI / Essensys / similar property-management portals. We sit ALONGSIDE them as the tenant business-intelligence layer.", colour: "text-[#00E676]" },
];

export default function IWG() {
  usePageview("/iwg");
  useNoIndex();

  const [form, setForm] = useState({ email: "", name: "", role: "", interest: "", building: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Work email is required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        email: form.email,
        name: form.name,
        company: "IWG",
        role: form.role,
        message: [
          form.interest ? `Interest: ${form.interest}` : "",
          form.building ? `Building / cohort: ${form.building}` : "",
          form.message ? `Notes: ${form.message}` : "",
        ].filter(Boolean).join("\n"),
        industry: "iwg",
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("Received — DARA founder will reach out within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const briefUrl = `${API}/public/iwg-tenant-brief`;
  const onePagerUrl = `${API}/public/overview-one-pager`;
  const benchmarkUrl = `${API}/public/ai-maturity-benchmark`;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="iwg-page">
      <BespokePageRibbon prospectName="IWG · Mercedes Bankston" />
      <BespokeWatermark pageLabel="IWG · MERCEDES" position="top" />
      {/* Top nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="back-to-home">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-2 hidden sm:inline">
              · IWG × DARA · BESPOKE PROPOSAL
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <a
              href={briefUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-download-brief"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] flex items-center gap-1.5"
            >
              <FileDown className="w-3.5 h-3.5" /> 2-page brief
            </a>
            <a
              href="#engage"
              data-testid="nav-engage"
              className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90"
            >
              Continue the conversation
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFB000]/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4">
            // BESPOKE PROPOSAL · PREPARED FOR IWG · TENANT NETWORK · {new Date().toLocaleString("en-AU", { month: "short", year: "numeric" }).toUpperCase()}
          </div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 mb-3">
            IWG <span className="text-[#FFB000]">×</span> DARA OS
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-5xl">
            An intelligence layer for the <span className="text-[#FFB000]">175,000-SMB tenant network</span>.
          </h1>
          <p className="text-base sm:text-lg text-zinc-300 mt-6 max-w-3xl leading-relaxed">
            IWG operates ~3,500 properties across ~120 countries. The typical Spaces / Regus property hosts 30-200 SMB tenants — the same operators DARA OS was built for. Today they pay IWG for space. <span className="text-white font-bold">Almost none monetise the data they capture daily.</span> DARA changes that asymmetry. The commercial story for IWG is straightforward: tenants who get more from their data stay longer, upgrade tiers, and refer peers more reliably.
          </p>

          <div className="flex flex-wrap gap-2 mt-7" data-testid="hero-meta-pills">
            {["~3,500 PROPERTIES", "~175k SMB TENANTS", "120 COUNTRIES", "WHITE-LABEL", "AU + UK + US SOVEREIGN", "DARA ENGINE", "READ-ONLY"].map(p => (
              <span key={p} className="px-2.5 py-1 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5 font-mono text-[10px] uppercase tracking-widest">{p}</span>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <a
              href="#engage"
              data-testid="hero-engage-cta"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              <ChevronRight className="w-4 h-4" /> Scope a pilot building <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={briefUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-download-brief"
              className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] underline-offset-4 hover:underline flex items-center gap-1.5"
            >
              <FileDown className="w-3.5 h-3.5" /> Download the 2-page brief
            </a>
          </div>
        </div>
      </section>

      {/* The wedge */}
      <section className="border-b border-zinc-900 bg-[#FFB000]/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// THE WEDGE · IN ONE PARAGRAPH</div>
          <p className="text-xl sm:text-2xl font-heading font-bold leading-snug max-w-5xl tracking-tight">
            <span className="text-[#FFB000]">IWG gets</span> distribution-grade tenant-retention lift, premium-tier upsell support, and a brand-safe always-on data-intelligence layer — <span className="text-[#FFB000]">DARA gets</span> distribution into 175,000 SMBs at near-zero customer-acquisition cost. <span className="text-zinc-400">Both sides win without either spending capital first.</span>
          </p>
        </div>
      </section>

      {/* Tenant types */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// WHO INSIDE IWG GETS THE MOST FROM DARA</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Tenant cohorts that DARA already serves. <span className="text-[#FFB000]">Plug-and-play, not greenfield.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="tenant-types-grid">
          {TENANT_TYPES.map(({ Icon, name, pitch }) => (
            <div key={name} className="bg-black p-6">
              <Icon className="w-5 h-5 text-[#FFB000] mb-3" />
              <div className="font-heading font-bold text-base tracking-tight">{name}</div>
              <p className="text-xs text-zinc-400 leading-relaxed mt-2">{pitch}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SMB video */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-zinc-900" data-testid="iwg-video-section">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-3">// 23 SECONDS · WHAT TENANTS SEE</div>
        <div>
          <VideoAd
            src={VIDEO_SMB}
            label="// SMB · 23s · TENANT-FACING PITCH"
            caption="// 11 JURISDICTION PLAYBOOKS · 14-DAY FREE TRIAL · NO CARD REQUIRED · WHITE-LABEL-READY FOR IWG"
            testId="iwg-video"
          />
        </div>
      </section>

      {/* On-ramps — every tenant has a path, honest about MVP vs roadmap */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// FOUR ON-RAMPS · NO TENANT IS EXCLUDED</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Every tenant has a path into DARA. <span className="text-[#FFB000]">Honest about MVP vs roadmap.</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-3 max-w-3xl leading-relaxed">
          IWG tenants don't all run Xero / Square / MyOB. ~70-80% will land on the CSV path. The four on-ramps below cover essentially every SMB stack — even cash-only operators with no formal POS. Status marks are explicit: ✓ live today, → on roadmap.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="on-ramps-grid">
          {ON_RAMPS.map(({ code, label, fit, flow, status, coverage }) => {
            const isLive = status === "live";
            return (
              <div key={code} className="bg-black p-6" data-testid={`on-ramp-${code}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// {code}</div>
                  <span
                    className={
                      isLive
                        ? "font-mono text-[9px] uppercase tracking-widest text-[#00E676] border border-[#00E676]/40 bg-[#00E676]/10 px-2 py-0.5"
                        : "font-mono text-[9px] uppercase tracking-widest text-[#FFB000] border border-[#FFB000]/40 bg-[#FFB000]/10 px-2 py-0.5"
                    }
                  >
                    {isLive ? "✓ LIVE TODAY" : "→ ROADMAP"}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-lg tracking-tight">{label}</h3>
                <div className="mt-3 space-y-2 text-xs">
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">WHO IT FITS</div>
                    <div className="text-zinc-300">{fit}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">WHAT IT LOOKS LIKE</div>
                    <div className="text-zinc-300">{flow}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mb-0.5">TENANT COVERAGE</div>
                    <div className="text-[#FFB000] font-bold">{coverage}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-6 border border-[#00E676]/30 bg-[#00E676]/[0.04] p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E676] mb-1">// THE COMBINED COVERAGE TODAY</div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Native connectors + CSV path together cover <b>~85-90% of any IWG tenant cohort today</b>. Email-in and manual-entry on-ramps unlock the remaining 10-15% by Q3 2026. <b>No prospect Mercedes introduces gets a "we can't help you" answer</b> — every tenant has a path.
          </p>
        </div>
      </section>

      {/* Pilot */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// PROPOSED PILOT · 90 DAYS · ONE BUILDING</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          One named building. <span className="text-[#FFB000]">15-25 tenants.</span> 90 days. Success bar pre-committed.
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="pilot-phases">
          {PILOT_PHASES.map(({ week, title, body }) => (
            <div key={week} className="bg-black p-6">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// {week}</div>
              <div className="font-heading font-bold text-lg tracking-tight">{title}</div>
              <p className="text-xs text-zinc-300 leading-relaxed mt-2">{body}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 border border-[#00E676]/30 bg-[#00E676]/[0.04] p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E676] mb-1">// PRE-COMMITTED SUCCESS BAR</div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            ≥70% weekly active · ≥3 measurable revenue or cost insights per tenant per month · ≥40 NPS · ≥2 of 25 tenants converted to paid DARA · IWG willingness to roll out to 5 additional properties.
          </p>
        </div>
      </section>

      {/* Deal models */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// COMMERCIAL · ZERO IWG CAPITAL ON MODELS A + B</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Three deal shapes. <span className="text-[#FFB000]">Start with A — IWG signs no PO.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="deal-models">
          {DEAL_MODELS.map(({ code, name, setup, success, risk, upside, highlight }) => (
            <div key={code} className={`p-6 sm:p-7 ${highlight ? "bg-[#FFB000]/5" : "bg-black"}`} data-testid={`deal-model-${code}`}>
              <div className="flex items-baseline gap-3 mb-3">
                <span className={`font-heading font-black text-4xl tracking-tighter ${highlight ? "text-[#FFB000]" : "text-zinc-700"}`}>{code}</span>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODEL</div>
                {highlight && <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-[#FFB000] border border-[#FFB000]/40 bg-[#FFB000]/10 px-1.5 py-0.5">RECOMMENDED START</span>}
              </div>
              <h3 className="font-heading font-bold text-lg tracking-tight">{name}</h3>
              <div className="mt-4 space-y-3 text-xs leading-relaxed">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">SETUP</div>
                  <div className="text-zinc-300">{setup}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">SUCCESS BAR</div>
                  <div className="text-zinc-300">{success}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">IWG RISK</div>
                  <div className="text-zinc-300">{risk}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mb-0.5">DARA UPSIDE</div>
                  <div className="text-[#FFB000] font-bold">{upside}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Honest limits */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// WHAT WE WILL NOT OVERSELL</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Honest about today. <span className="text-[#FFB000]">Audit-defensible from day one.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="honest-limits">
          {HONEST_LIMITS.map(({ Icon, label, colour }, i) => (
            <div key={i} className="bg-black p-6 flex items-start gap-3">
              <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${colour}`} />
              <p className="text-sm text-zinc-300 leading-relaxed">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Engage */}
      <section id="engage" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="border border-[#FFB000]/40 bg-[#FFB000]/[0.03] p-6 sm:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-3">// CONTINUE THE CONVERSATION</div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
            One building. <span className="text-[#FFB000]">90 days.</span> $0 to IWG.
          </h2>
          <p className="text-sm text-zinc-300 mt-3 max-w-3xl leading-relaxed">
            Submit details. DARA's founder will reach out within one business day with three nominated tenant cohorts inside the building, a 90-day success-bar draft, and a mutual NDA. No procurement engagement until both sides see signal.
          </p>

          {!submitted && (
            <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="iwg-engage-form">
              <Input label="Work email *" testid="iwg-email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Input label="Full name" testid="iwg-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Input label="Role / title" testid="iwg-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
              <div>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Tenant cohort of immediate interest</span>
                  <select
                    value={form.interest}
                    onChange={(e) => setForm({ ...form, interest: e.target.value })}
                    data-testid="iwg-interest"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  >
                    <option value="">— Pick one —</option>
                    <option value="cafes">Cafés & food merchants</option>
                    <option value="micro-retail">Micro-retailers & boutiques</option>
                    <option value="training">Training schools & coaching</option>
                    <option value="professional">Professional services & consultancies</option>
                    <option value="mixed">Mixed cohort — let DARA scope from your tenant mix</option>
                  </select>
                </label>
              </div>
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Building / property of focus (optional)</span>
                  <input
                    type="text"
                    value={form.building}
                    onChange={(e) => setForm({ ...form, building: e.target.value })}
                    data-testid="iwg-building"
                    placeholder="e.g. Spaces · Sydney CBD · Pitt St · or 'TBD, want DARA to recommend'"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  />
                </label>
              </div>
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Anything specific to flag (optional)</span>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="iwg-message"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  />
                </label>
              </div>
              <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-3 pt-2">
                <p className="font-mono text-[10px] text-zinc-600 max-w-md">
                  By submitting you accept that DARA will contact you about this pilot. Mutual NDA available pre-pilot.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="iwg-submit"
                  className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-40"
                >
                  {submitting ? <><Sparkles className="w-3.5 h-3.5 animate-pulse" /> SENDING…</> : <><ChevronRight className="w-3.5 h-3.5" /> Send to DARA founder</>}
                </button>
              </div>
            </form>
          )}

          {submitted && (
            <div className="mt-8 border border-[#00E676]/40 bg-[#00E676]/5 p-6" data-testid="iwg-success">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#00E676] mb-2">
                <CheckCircle2 className="w-3.5 h-3.5" />
                REQUEST RECEIVED
              </div>
              <h3 className="font-heading font-bold text-2xl tracking-tight">You're on the list.</h3>
              <p className="text-sm text-zinc-300 mt-2 max-w-xl leading-relaxed">
                DARA's founder will reach out within one business day. In the meantime, share the <a href={briefUrl} target="_blank" rel="noopener noreferrer" className="text-[#FFB000] underline">2-page IWG × DARA brief</a> with your tenant operations lead.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Documents</div>
            <ul className="space-y-2">
              <li><a href={briefUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">IWG × DARA Brief</a></li>
              <li><a href={onePagerUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">DARA 1-Pager</a></li>
              <li><a href={benchmarkUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">AU AI Maturity Benchmark</a></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Related</div>
            <ul className="space-y-2">
              <li><Link to="/founders-guide" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Founder's Guide</Link></li>
              <li><Link to="/pricing" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Pricing</Link></li>
              <li><Link to="/" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Homepage</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Trust</div>
            <ul className="space-y-2">
              <li><Link to="/security" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Security & Trust</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">DARA OS</div>
            <p className="text-[11px] text-zinc-600 leading-relaxed">
              The Bloomberg-terminal for enterprise operators. Built in Australia, deployed worldwide.
            </p>
            <p className="text-[10px] text-zinc-700 leading-relaxed mt-3 font-mono">
              Bespoke proposal · prepared for IWG · not for redistribution outside IWG leadership without DARA permission.
            </p>
          </div>
        </div>
        <div className="border-t border-zinc-900 px-4 sm:px-6 py-4 max-w-7xl mx-auto flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">© DARA OS · {new Date().getFullYear()}</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-700">IWG × DARA · v1</span>
        </div>
      </footer>
      <BespokeWatermark pageLabel="IWG · MERCEDES" position="bottom" />
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}
